import React from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';

import { Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import CustomOfferMerchantFeeField from '../CustomOfferMerchantFeeField';
import { CustomerPaymentSectionState, PaymentFlowV2FieldErrorMap } from '../paymentFlowUtils';

import V1CustomerPaymentFields from './V1CustomerPaymentFields';
import V2CustomerPaymentFields from './V2CustomerPaymentFields';

type Props = {
  control: Control<CustomOfferFormValues>;
  register: UseFormRegister<CustomOfferFormValues>;
  errors: FieldErrors<CustomOfferFormValues>;
  setValue: UseFormSetValue<CustomOfferFormValues>;
  paymentFlowV2Errors: PaymentFlowV2FieldErrorMap | undefined;
  hasOrders?: boolean;
  isSubmitting: boolean;
  isCustomOfferPackage: boolean;
  isBusinessTraveller: boolean;
  isEditMode: boolean;
  user: App.User;
  offer?: App.CustomOffer;
  price: number | null | undefined;
  customerDepositAmount: number | null | undefined;
  paymentDate?: string;
  sectionState: CustomerPaymentSectionState;
  sectionTitle?: string;
  showLegacyPaymentFields?: boolean;
  showMerchantFee?: boolean;
  showManualDepositOption?: boolean;
  showDepositMenuItem?: boolean;
  paymentFlowHelperText?: string;
  forceFullPayment?: boolean;
};

export default function CustomerPaymentsSection({
  control,
  register,
  errors,
  setValue,
  paymentFlowV2Errors,
  hasOrders,
  isSubmitting,
  isCustomOfferPackage,
  isBusinessTraveller,
  isEditMode,
  user,
  offer,
  price,
  customerDepositAmount,
  paymentDate,
  sectionState,
  sectionTitle = 'Customer payments (Payment made by customer)',
  showLegacyPaymentFields = true,
  showMerchantFee = true,
  showManualDepositOption = true,
  showDepositMenuItem = true,
  paymentFlowHelperText,
  forceFullPayment = false,
}: Props) {
  const {
    isV2FullPayment,
    isV2PaymentSchedule,
    showV2ManualFields,
    showV2PaymentFlowSelector,
    showLegacyDepositFields,
    showLegacyFinalAmount,
    showLegacyPaymentTypeSelector,
  } = sectionState;

  return (
    <>
      <Box mt={2}>
        <Typography variant="h6">{sectionTitle}</Typography>
      </Box>
      <Grid mt={2} container columns={6} spacing={2}>
        {showLegacyPaymentFields && showLegacyPaymentTypeSelector && (
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Payment type"
                  disabled={hasOrders || isSubmitting || isCustomOfferPackage || forceFullPayment}
                  select
                  fullWidth
                  helperText={errors.payment_metadata?.payment_type?.message}
                  error={!!errors?.payment_metadata?.payment_type}
                  data-testid="customer-payment-type"
                >
                  <MenuItem value="deposit">Deposit</MenuItem>
                  <MenuItem value="full_payment">Full Payment</MenuItem>
                </TextField>
              )}
            />
          </Grid>
        )}
        {showV2PaymentFlowSelector && (
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_flow_v2.type"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Payment flow"
                  disabled={hasOrders || isSubmitting || forceFullPayment}
                  select
                  fullWidth
                  helperText={error?.message ?? paymentFlowHelperText}
                  error={!!error}
                  data-testid="deposit-flow"
                >
                  <MenuItem value="full_payment">Full payment</MenuItem>
                  {showDepositMenuItem && (
                    <MenuItem value="payment_schedule">Deposit (customer pays via My Escapes)</MenuItem>
                  )}
                  {showManualDepositOption && (
                    <MenuItem value="manual">Deposit (manual - agent takes deposit)</MenuItem>
                  )}
                </TextField>
              )}
            />
          </Grid>
        )}
        <Grid size={{ xs: 6, md: 1 }}>
          <Controller
            name="payment_metadata.block_customer_payment_notifications"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} data-testid="dont-notify-customer-on-payment" />}
                label="Don't notify customer on payment"
              />
            )}
          />
        </Grid>
        {showMerchantFee && (
          <Grid size={{ xs: 6, md: 2 }}>
            <CustomOfferMerchantFeeField
              isBusinessTraveller={isBusinessTraveller}
              userId={user.id_member}
              offerId={offer?.id}
              control={control}
              setValue={setValue}
              hasOrders={hasOrders}
              isSubmitting={isSubmitting}
            />
          </Grid>
        )}
      </Grid>
      <V2CustomerPaymentFields
        control={control}
        register={register}
        paymentFlowV2Errors={paymentFlowV2Errors}
        hasOrders={hasOrders}
        isSubmitting={isSubmitting}
        isV2FullPayment={isV2FullPayment}
        isV2PaymentSchedule={isV2PaymentSchedule}
        showV2ManualFields={showV2ManualFields}
      />
      {showLegacyPaymentFields && (
        <V1CustomerPaymentFields
          control={control}
          register={register}
          errors={errors}
          hasOrders={hasOrders}
          isSubmitting={isSubmitting}
          isEditMode={isEditMode}
          price={price}
          customerDepositAmount={customerDepositAmount}
          paymentDate={paymentDate}
          showLegacyDepositFields={showLegacyDepositFields}
          showLegacyFinalAmount={showLegacyFinalAmount}
        />
      )}
    </>
  );
}
